import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import api from "@/lib/api";
import Ornament from "@/components/Ornament";
import ShareButtons from "@/components/ShareButtons";
import NewsletterSignup from "@/components/NewsletterSignup";

const HERO = "/photos/prieure-cross.jpg";
const PIL_IMG = "/photos/prieure-meadow.jpg";
const TWIN_IMG = "/photos/prieure-leaves.jpg";

// Target date: Fêtes populaires - opening day, 1 July 2027 at 17:00 Paris
const FETES_DATE = new Date("2027-07-01T17:00:00+02:00");

function Countdown({ locale = "fr" }) {
  const compute = () => {
    const diff = Math.max(0, FETES_DATE.getTime() - Date.now());
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { days, hours, minutes, seconds };
  };
  const [time, setTime] = useState(compute);
  useEffect(() => {
    const id = setInterval(() => setTime(compute()), 1000);
    return () => clearInterval(id);
  }, []);
  const labels = locale === "fr"
    ? { days: "Jours", hours: "Heures", minutes: "Minutes", seconds: "Secondes", towards: "Vers les Fêtes de saint Léonard" }
    : { days: "Days", hours: "Hours", minutes: "Minutes", seconds: "Seconds", towards: "Towards the Feasts of Saint Léonard" };
  const pad = (n) => String(n).padStart(2, "0");
  const cells = [
    { v: time.days, l: labels.days },
    { v: pad(time.hours), l: labels.hours },
    { v: pad(time.minutes), l: labels.minutes },
    { v: pad(time.seconds), l: labels.seconds },
  ];
  return (
    <div data-testid="presence-countdown" className="mt-6">
      <div className="font-caps text-[0.62rem] mb-2.5 text-white/75" style={{ letterSpacing: "0.28em" }}>
        {labels.towards}
      </div>
      <div className="flex flex-wrap gap-2 sm:gap-2.5">
        {cells.map((c, i) => (
          <div key={i} className="flex flex-col items-center min-w-[52px] sm:min-w-[58px] px-2 sm:px-3 py-1.5 sm:py-2"
               style={{
                 background: "rgba(20,16,12,0.4)",
                 border: "1px solid rgba(212,175,55,0.4)",
                 backdropFilter: "blur(6px)",
               }}>
            <div className="font-display text-lg sm:text-2xl text-white tabular-nums leading-none">{c.v}</div>
            <div className="font-caps text-[0.48rem] sm:text-[0.55rem] mt-0.5 text-white/70" style={{ letterSpacing: "0.18em" }}>
              {c.l}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PresenceSubNav({ locale }) {
  const items = locale === "fr"
    ? [
        { id: "origine", label: "Origine" },
        { id: "programme", label: "Programme" },
        { id: "services", label: "Services" },
        { id: "contacts", label: "Contacts" },
      ]
    : [
        { id: "origine", label: "Origins" },
        { id: "programme", label: "Programme" },
        { id: "services", label: "Services" },
        { id: "contacts", label: "Contacts" },
      ];

  const onClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const rect = el.getBoundingClientRect();
      window.scrollTo({ top: window.scrollY + rect.top - 70, behavior: "smooth" });
    }
  };

  return (
    <nav
      data-testid="presence-subnav"
      className="sticky top-0 z-30"
      style={{
        background: "rgba(245,240,228,0.97)",
        backdropFilter: "blur(8px)",
        borderBottom: "1px solid var(--border)",
      }}
    >
      <div className="container-prieure flex items-center justify-center sm:justify-start overflow-x-auto gap-1 sm:gap-2 py-3">
        {items.map((it) => (
          <a
            key={it.id}
            href={`#${it.id}`}
            data-testid={`subnav-${it.id}`}
            onClick={(e) => onClick(e, it.id)}
            className="font-caps text-[0.7rem] px-4 py-2 whitespace-nowrap transition-colors hover:bg-[var(--cinnabar)] hover:text-white"
            style={{
              letterSpacing: "0.2em",
              color: "var(--ink)",
              border: "1px solid transparent",
            }}
          >
            {it.label}
          </a>
        ))}
      </div>
    </nav>
  );
}

const COUNTRIES = [
  "France", "Italie", "Belgique", "Suisse", "Allemagne", "Royaume-Uni", "Espagne",
  "Pays-Bas", "Luxembourg", "Portugal", "Irlande", "Pologne", "États-Unis", "Canada",
  "Autriche", "Norvège", "Suède", "Danemark", "Autre",
];

const colorMap = {
  cinnabar: "var(--cinnabar)",
  gold: "var(--gold)",
  lapis: "var(--lapis)",
};

// `PreRegistrationSection` removed in Iter Y — the single canonical form lives
// on the dedicated `/presence/pre-inscription` page (FetesPreInscription).


const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveAsset = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);

export default function Presence() {
  const { t, locale } = useI18n();
  const p = t.presence;
  // const [partners, setPartners] = useState([]);  // Removed in Iter Z+ — partners
  // are now showcased only via the global PartnersMarquee in the Footer.
  const [fetesPhotos, setFetesPhotos] = useState([]);
  const [slide, setSlide] = useState(0);
  useEffect(() => {
    api.get("/photos", { params: { on_fetes: true } })
      .then((r) => setFetesPhotos(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (fetesPhotos.length <= 1) return undefined;
    setSlide(0);
    const id = setInterval(() => setSlide((s) => (s + 1) % fetesPhotos.length), 6500);
    return () => clearInterval(id);
  }, [fetesPhotos.length]);

  // If admin has selected photos for the Fêtes slideshow, use the first as the
  // hero background and cycle through. Otherwise fall back to the bundled HERO.
  const heroSrc = fetesPhotos.length > 0
    ? resolveAsset(fetesPhotos[slide]?.image_url || fetesPhotos[0].image_url)
    : HERO;

  return (
    <div data-testid="presence-page">
      {/* HERO */}
      <section className="relative" style={{ height: "100vh", minHeight: 600 }}>
        <img src={heroSrc} alt="" className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000" />
        <div className="absolute inset-0 hero-vignette" />
        {fetesPhotos.length > 1 && (
          <div className="absolute bottom-6 right-6 flex gap-2 z-10" data-testid="fetes-slide-dots">
            {fetesPhotos.map((_, i) => (
              <button key={i} onClick={() => setSlide(i)} aria-label={`Slide ${i + 1}`}
                      className="w-2 h-2 rounded-full transition-all"
                      style={{
                        background: i === slide ? "var(--gold)" : "rgba(255,255,255,0.45)",
                        outline: i === slide ? "1px solid rgba(255,255,255,0.6)" : "none",
                      }} />
            ))}
          </div>
        )}

        <div className="container-prieure relative h-full flex flex-col justify-end pb-8 sm:pb-10">
          <div className="flex items-center gap-6 sm:gap-10" data-testid="fetes-title-row">
            <div className="flex-1 flex flex-col items-end text-right">
              <h1
                className="font-display text-3xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[1.02]"
                style={{ letterSpacing: "0.03em" }}
              >
                {locale === "fr" ? "FÊTES DE SAINT LÉONARD" : "FEASTS OF SAINT LÉONARD"}
              </h1>
              <p className="font-display italic text-xl sm:text-2xl lg:text-3xl mt-3 text-white/95">
                {locale === "fr"
                  ? "Là où l'expérience sensorielle du Profane rejoint le Sacré"
                  : "Where the sensory experience of the Profane meets the Sacred"}
              </p>
            </div>
            <img
              src="/photos/millenium-vertical.png"
              alt="Millenium 2027 — Année Européenne des Normands"
              data-testid="presence-millenium-logo"
              style={{
                background: "rgba(255,255,255,0.95)",
                padding: "8px",
                boxShadow: "0 6px 18px rgba(0,0,0,0.4)",
                border: "1px solid rgba(255,255,255,0.7)",
                aspectRatio: "1 / 1",
                height: "clamp(7rem, 14vw, 11rem)",
                width: "auto",
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
          </div>
          <div className="font-caps text-[0.72rem] mt-5 text-white/85 text-right" style={{ letterSpacing: "0.22em" }}>
            {locale === "fr"
              ? "2 — 4 juillet 2027 · Avranches · Saint Léonard · Mont Saint-Michel · Normandie"
              : "1 → 5 July 2027 · Avranches · Prieuré saint Léonard · Mont-Saint-Michel · Normandie"}
          </div>
        </div>

        {/* Mobile millenium logo at bottom-left of hero */}
        <div className="absolute left-6 bottom-6 z-10 md:hidden">
          <img
            src="/photos/millenium-vertical.png"
            alt="Millenium 2027 — Année Européenne des Normands"
            style={{ width: 90, height: "auto" }}
          />
        </div>
      </section>


      {/* INTRO */}
      <section className="py-24 sm:py-32 section-edge-top">
        <div className="container-prieure max-w-4xl">
          <Ornament className="mb-10" />
          <p className="font-display italic text-2xl sm:text-3xl leading-snug text-center" style={{ color: "var(--ink)" }}>
            {p.intro}
          </p>
        </div>
      </section>

      {/* 1087 — L'acte fondateur (Origines) */}
      <section
        id="origine"
        data-testid="presence-founding-act"
        className="py-24 sm:py-32 relative"
        style={{ background: "var(--ink)", color: "white" }}
      >
        <div className="container-prieure grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5 text-center md:text-left">
            <div className="font-caps text-[0.7rem] mb-4" style={{ color: "var(--gold)", letterSpacing: "0.3em" }}>
              {locale === "fr" ? "Le millénaire d'une donation" : "The millennium of a donation"}
            </div>
            <div
              className="font-display leading-none"
              style={{
                fontSize: "clamp(4rem, 14vw, 11rem)",
                color: "var(--gold)",
                letterSpacing: "-0.04em",
              }}
            >
              1087
            </div>
            <div className="font-display text-2xl mt-2" style={{ color: "rgba(255,255,255,0.6)" }}>
              — 2027 —
            </div>
            <div
              className="font-caps text-[0.7rem] mt-6"
              style={{ color: "var(--gold)", letterSpacing: "0.25em" }}
            >
              {locale === "fr" ? "13 Septembre 1087" : "13 September 1087"}
            </div>
            <div className="font-display italic text-xl mt-2" style={{ color: "rgba(255,255,255,0.9)" }}>
              {locale === "fr" ? "L'acte fondateur" : "The founding act"}
            </div>
          </div>
          <div className="md:col-span-7 md:border-l md:pl-12" style={{ borderColor: "rgba(212,175,55,0.3)" }}>
            <h2 className="font-display text-4xl sm:text-5xl leading-tight">
              {locale === "fr"
                ? "Mille ans après Guillaume."
                : "A thousand years after William."}
            </h2>
            <p className="font-body text-lg mt-6" style={{ color: "rgba(255,255,255,0.85)" }}>
              {locale === "fr"
                ? "Né en 1027 à Falaise et mort le 9 septembre 1087 à Rouen, Guillaume le Conquérant offrait, peu avant sa disparition, le Prieuré saint Léonard de Vains à l'Abbaye aux Hommes de Caen. Neuf siècles plus tard, le programme PRESENCE — à l'origine des FÊTES DE SAINT LÉONARD dans le cadre du MILLENIUM — rend cette mémoire sensible, vivante, partagée par l'art, la spiritualité, la transmission (patrimoines, traditions médiévales…) et la rencontre."
                : "Born in 1027 in Falaise and died on 9 September 1087 in Rouen, William the Conqueror granted, shortly before his death, the Priory of Saint Léonard of Vains to the Abbaye aux Hommes in Caen. Nine centuries later, the PRESENCE programme — at the origin of the FEASTS OF SAINT LÉONARD within MILLENIUM — makes this memory tangible, living, shared through art, spirituality, transmission (heritage, medieval traditions…) and encounter."}
            </p>
            <div className="mt-8 flex items-center gap-4">
              <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.3)" }} />
              <div className="font-display text-2xl" style={{ color: "var(--gold)" }}>✦</div>
              <div className="h-px flex-1" style={{ background: "rgba(212,175,55,0.3)" }} />
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-20 sm:py-28" style={{ background: "var(--parchment-2)" }}>
        <div className="container-prieure">
          <div className="font-caps text-[0.72rem] mb-3" style={{ color: "var(--cinnabar)" }}>
            {p.pillarsTitle}
          </div>
          <h2 className="font-display text-4xl sm:text-5xl mb-12">{p.pillarsTitle}</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {p.pillars.map((pil, i) => (
              <article
                key={i}
                data-testid={`presence-pillar-${i}`}
                className="bg-white p-8 border flex gap-6"
                style={{ borderColor: "var(--border)" }}
              >
                <div className="font-display text-5xl shrink-0" style={{ color: "var(--cinnabar)" }}>
                  {String.fromCharCode(8544 + i) /* Roman: Ⅰ Ⅱ Ⅲ Ⅳ */}
                </div>
                <div>
                  <h3 className="font-display text-2xl mb-2">{pil.title}</h3>
                  <p className="font-body" style={{ color: "var(--ink-2)" }}>{pil.body}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FRESQUES — heart of the project */}
      <section
        id="fresques"
        data-testid="presence-fresques"
        className="py-24 sm:py-32"
        style={{ background: "var(--parchment)" }}
      >
        <div className="container-prieure grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* LEFT: copy */}
          <div className="md:col-span-7 md:col-start-3">
            <Ornament className="justify-start mb-6" />
            <div className="font-caps text-[0.72rem] mb-3" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>
              {p.fresquesKicker}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              {p.fresquesTitle}
            </h2>
            <p className="font-body text-lg mt-6" style={{ color: "var(--ink-2)" }}>
              {p.fresquesBody}
            </p>
            <div className="mt-10 border-l-2 pl-5" style={{ borderColor: "var(--gold)" }}>
              <div className="font-display italic text-xl" style={{ color: "var(--ink)" }}>
                {p.fresquesQuote}
              </div>
              <div className="font-caps text-[0.65rem] mt-2" style={{ color: "var(--muted)", letterSpacing: "0.2em" }}>
                {p.fresquesStudies}
              </div>
            </div>
            <Link
              to="/presence/pre-inscription"
              data-testid="fresques-cta"
              className="btn btn-outline mt-10 inline-block"
            >
              {p.fresquesCta}
            </Link>
          </div>

          {/* THEMES STRIP */}
          <div className="md:col-span-12 mt-8 lg:mt-12 pt-10 border-t" style={{ borderColor: "var(--border)" }}>
            <div className="font-caps text-[0.72rem] mb-6 text-center" style={{ color: "var(--cinnabar)", letterSpacing: "0.25em" }}>
              {locale === "fr" ? "Les six thèmes" : "The six themes"}
            </div>
            <ol className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6">
              {(p.fresquesThemes || []).map((theme, i) => (
                <li
                  key={i}
                  data-testid={`fresque-theme-${i}`}
                  className="text-center group cursor-default"
                >
                  <div
                    className="font-display text-3xl mb-2 transition-colors"
                    style={{ color: "var(--gold)" }}
                  >
                    {String.fromCharCode(8544 + i) /* Ⅰ Ⅱ Ⅲ Ⅳ Ⅴ Ⅵ */}
                  </div>
                  <div className="font-display text-base leading-tight" style={{ color: "var(--ink)" }}>
                    {theme}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* EVENT 3 days (Programme) */}
      <section id="programme" className="py-24 sm:py-32">
        <div className="container-prieure">
          <div className="text-center mb-16">
            <div className="font-caps text-[0.72rem] mb-3" style={{ color: "var(--gold)" }}>
              {locale === "fr" ? "L'évènement central" : "The central event"}
            </div>
            <h2 className="font-display text-4xl sm:text-6xl">{p.eventTitle}</h2>
            <p className="font-display italic text-xl mt-4" style={{ color: "var(--ink-2)" }}>
              {p.eventSubtitle}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/presence/programme.pdf?locale=${locale}`}
                target="_blank" rel="noreferrer"
                data-testid="public-programme-pdf"
                className="btn btn-primary"
              >
                📄 {locale === "fr" ? "Télécharger le programme" : "Download programme"}
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/presence/livret.pdf?locale=${locale}`}
                target="_blank" rel="noreferrer"
                data-testid="public-livret-pdf"
                className="btn btn-outline"
              >
                📖 {locale === "fr" ? "Livret du participant" : "Participant booklet"}
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/presence/poster.pdf?locale=${locale}&size=A2`}
                target="_blank" rel="noreferrer"
                data-testid="public-poster-a2-pdf"
                className="btn btn-outline"
              >
                🖼 {locale === "fr" ? "Affiche A2 imprimable" : "Printable A2 poster"}
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/presence/programme-all.zip`}
                target="_blank" rel="noreferrer"
                data-testid="public-programme-all-zip"
                className="btn btn-outline"
              >
                🌐 {locale === "fr" ? "Programme · 19 langues (ZIP)" : "Programme · 19 languages (ZIP)"}
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/presence/livret-all.zip`}
                target="_blank" rel="noreferrer"
                data-testid="public-livret-all-zip"
                className="btn btn-outline"
              >
                📚 {locale === "fr" ? "Livret · 19 langues (ZIP)" : "Booklet · 19 languages (ZIP)"}
              </a>
            </div>
            <ShareButtons locale={locale} />
            <NewsletterSignup locale={locale} />
          </div>

          {/* 3-day detailed schedule grid removed — to be rebuilt with admin-managed entries */}
        </div>
      </section>

      {/* UNESCO OSTENSIONS HIGHLIGHT */}
      <section className="relative py-24" style={{ background: "var(--ink)" }}>
        <div className="container-prieure grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-3">
            <a
              href="https://ich.unesco.org/fr/RL/les-ostensions-septennales-limousines-00521"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="unesco-logo-link"
              className="block bg-white p-6 transition-transform hover:scale-[1.02]"
              style={{ borderRadius: "4px" }}
              aria-label="UNESCO — Patrimoine culturel immatériel de l'humanité"
            >
              <img
                src="/unesco-pci.png"
                alt="UNESCO — Patrimoine culturel immatériel"
                className="w-full h-auto"
                style={{ display: "block" }}
              />
            </a>
          </div>
          <div className="md:col-span-9">
            <div className="font-caps text-[0.72rem] mb-3" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>
              {p.unescoBadge} · {locale === "fr" ? "septennales" : "septennial"}
            </div>
            <h2 className="font-display text-5xl sm:text-6xl text-white mb-5"
                style={{ letterSpacing: "0.04em", textTransform: "uppercase", fontWeight: 500 }}>
              {locale === "fr" ? "Ostensions" : "Ostensions"}
            </h2>
            <p className="font-body text-lg text-white/80 max-w-3xl">
              {p.unescoBody}
            </p>
          </div>
        </div>
      </section>

      {/* TWINNING + IMG */}
      <section className="py-20" style={{ background: "var(--parchment-2)" }}>
        <div className="container-prieure grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <img src={TWIN_IMG} alt="" className="w-full object-cover" style={{ aspectRatio: "4/3" }} />
          </div>
          <div className="md:col-span-6">
            <div className="font-caps text-[0.72rem] mb-3" style={{ color: "var(--lapis)" }}>
              {locale === "fr" ? "Coopération européenne" : "European cooperation"}
            </div>
            <h2 className="font-display text-4xl mb-4">
              {locale === "fr"
                ? "Vains ↔ Foggia : un jumelage millénaire"
                : "Vains ↔ Foggia: a millennial twinning"}
            </h2>
            <p className="font-body text-lg" style={{ color: "var(--ink-2)" }}>
              {locale === "fr"
                ? "L'abbaye San Leonardo de Siponto (Foggia, Pouilles, Italie), fondée par les bénédictins et mentionnée dès 1113, est dédiée à saint Léonard de Noblac — culte apporté en Italie du Sud par les Normands. Halte célèbre des pèlerins en route vers le Monte Sant'Angelo puis des croisés se rendant en Terre Sainte, elle passa en 1127 aux chanoines de saint Augustin, puis en 1261 aux chevaliers teutoniques. Notre prieuré sera jumelé avec elle le dimanche 4 juillet 2027."
                : "The abbey of San Leonardo de Siponto (Foggia, Apulia, Italy), founded by the Benedictines and first attested in 1113, is dedicated to Saint Leonard of Noblac — a cult brought to Southern Italy by the Normans. A renowned stop for pilgrims travelling to Monte Sant'Angelo and later for crusaders heading to the Holy Land, it was entrusted to the Augustinian canons in 1127 and to the Teutonic Knights in 1261. Our priory will be twinned with it in July 2027."}
            </p>
          </div>
        </div>
      </section>

      {/* PARTNERS — Section grid removed in Iter Z+ per user request. The scrolling
         partners ribbon (PartnersMarquee in the Footer) remains on every page,
         so partners are still showcased site-wide without taking a full section here. */}

      {/* PRE-REGISTRATION moved to dedicated page /presence/pre-inscription (Iter Y) */}

      {/* LIENS UTILES section removed — dedicated page /presence/liens-utiles in menu */}

      {/* SHARE — viral via Facebook & WhatsApp */}
      <section
        data-testid="presence-share"
        className="py-16"
        style={{ background: "var(--parchment-2)" }}
      >
        <div className="container-prieure">
          <div className="text-center max-w-2xl mx-auto">
            <div className="font-caps text-[0.7rem] mb-3" style={{ color: "var(--cinnabar)", letterSpacing: "0.3em" }}>
              {locale === "fr" ? "Faites rayonner" : "Spread the word"}
            </div>
            <h3 className="font-display text-3xl sm:text-4xl mb-4">
              {locale === "fr"
                ? "Partagez les Fêtes de saint Léonard"
                : "Share the Feasts of Saint Léonard"}
            </h3>
            <p className="font-display italic text-lg mb-8" style={{ color: "var(--ink-2)" }}>
              {locale === "fr"
                ? "Aidez le réseau européen des Normands à se rassembler en 2027."
                : "Help the European network of Normans gather in 2027."}
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== "undefined" ? window.location.href.split("#")[0] : "https://prieure-saint-leonard.fr/presence")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-facebook"
                className="inline-flex items-center gap-3 px-6 py-3 transition-all hover:scale-[1.03]"
                style={{
                  background: "#1877F2",
                  color: "white",
                  fontFamily: "var(--font-caps, inherit)",
                  fontSize: "0.7rem",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  borderRadius: 2,
                  boxShadow: "0 4px 14px rgba(24,119,242,0.35)",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.412c0-3.017 1.792-4.683 4.533-4.683 1.313 0 2.686.235 2.686.235v2.967h-1.513c-1.491 0-1.956.93-1.956 1.886v2.262h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
                </svg>
                Facebook
              </a>
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent((locale === "fr" ? "Découvrez les Fêtes de saint Léonard — Millenium 2027 : " : "Discover the Feasts of Saint Léonard — Millenium 2027: ") + (typeof window !== "undefined" ? window.location.href.split("#")[0] : "https://prieure-saint-leonard.fr/presence"))}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-whatsapp"
                className="inline-flex items-center gap-3 px-6 py-3 transition-all hover:scale-[1.03]"
                style={{
                  background: "#25D366",
                  color: "white",
                  fontFamily: "var(--font-caps, inherit)",
                  fontSize: "0.7rem",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                  borderRadius: 2,
                  boxShadow: "0 4px 14px rgba(37,211,102,0.35)",
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                </svg>
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACTS */}
      <section id="contacts" className="py-24 sm:py-32">
        <div className="container-prieure">
          <div className="max-w-2xl mx-auto text-center">
            <div className="deco-rule mb-6">
              <span className="font-caps text-[0.7rem]" style={{ color: "var(--ink)" }}>
                {p.contactBlock}
              </span>
            </div>
            <p className="font-display italic text-lg" style={{ color: "var(--ink-2)" }}>
              {locale === "fr"
                ? "Les inscriptions définitives ouvriront prochainement. En attendant, pré-inscrivez-vous pour être informé en priorité."
                : "Definitive registration will open soon. In the meantime, pre-register to be informed first."}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
